import React, { useEffect, useState, useRef } from "react";
import { ThemeProvider } from "@emotion/react";
import { CssBaseline, Pagination } from "@mui/material";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { setCookies, getCookie } from "cookies-next";

import TabPanel from "./components/edupix/tabpanel";
import SearchType from "./components/edupix/components/searchtype";
import InputSearch from "./components/edupix/components/inputsearch";
import { EdupixGalleryItem, GDLGalleryItem } from "./components/edupix/galleryitem";
import { FileItem } from "./components/edupix/components/fileitem";
import { GDLFileItem } from "./components/edupix/components/gdlfileitem";
import {
  useDebounce,
  eduSearch,
  gdlSearch,
  findOne,
  gdlFindOne,
  locales,
  gdlLocales,
  contextType,
} from "./components/edupix/utils";

import LocaleSelector from "./components/edupix/components/localeselector";
import theme from "./theme";

import "./css/style.css";

import {
  Container,
  ItemList,
  LinkItem,
  PaginationWrapper
} from "./style";
// import { TableView } from "@mui/icons-material";
/* const filterOptions = [
  // { name: "All", value: "search" },
  { name: "Image", value: "image" },
  // { name: "Audio", value: "audio" },
]; */

const getHashParams = () => {
  const urlHash = decodeURIComponent(window.location.hash.replace("#", ""));
  let params = new URLSearchParams(urlHash);
  const qo = {
    q: params.get("q") || "",
    f: params.get("f") || "featured",
    p: params.get("p") || 1,
    t: params.has("t") ? parseInt(params.get("t")) : contextType.EDUPIX,
    // locale: params.get("locale") || "en", //
    locale: params.get("locale") || getCookie("USER_LOCALE") || "en", // || "en",
  };
  if (params.has("fileItem")) {
    qo.fileItem = params.get("fileItem");
  }
  return { ...qo };
};

// build new url/hash on events
const buildHashUrl = (options = { q: "", f: "image", p: 1, locale: "en", t: 1 }) => {
  const urlParams = new URLSearchParams(options);
  return encodeURIComponent(urlParams.toString());
};

const pageSize = 20;

const App = ({ nonce }) => {
  const urlHashParams = getHashParams();
  const storedLocale = urlHashParams.locale || "en";
  const [locale, setLocale] = useState(storedLocale);
  const [meta, setMeta] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fileItem, setFileItem] = useState(null);
  const firstUpdate = useRef(true);
  // console.log('urlHashParams.t', urlHashParams.t)
  const [context, setContext] = useState(parseInt(urlHashParams.t));
  const [q, setQ] = useState(urlHashParams.q);
  const debouncedQ = useDebounce(q, 500);
  // const isEduSearch = searchType[context] === 
  // console.log('context', context, typeof context, '||', contextType.EDUPIX, typeof contextType.EDUPIX)
  const handleTabChange = (event, newValue) => {
    setResults([]);
    setMeta(null);
    const queryParams = getHashParams();
    const url = buildHashUrl({ ...queryParams, locale: "en", p: 1, t: newValue });
    window.location.hash = url;
  };

  const handleFilterChange = async (value) => {
    const queryParams = getHashParams();
    const url = buildHashUrl({ ...queryParams, p: 1, f: value });
    window.location.hash = url;
    setQ("");
    setFileItem(null);
  };

  const handleSearch = (value) => {
    setQ(value);
  };

  const loadFile = (fileId) => {
    setLoading(true);
    if (context === contextType.EDUPIX) {
      window.location.hash = `fileItem=${fileId}&t=0`;
      findOne(fileId).then((response) => {
        setFileItem(response);
        setLoading(false);
      });
    }
    if (context === contextType.GDL) {
      window.location.hash = `fileItem=${fileId}&t=1`;
      gdlFindOne(fileId).then((response) => {
        setFileItem(response);
        setLoading(false);
      });
    }
  };

  const handlePageChange = (event, page) => {
    const queryParams = getHashParams();
    const url = buildHashUrl({ ...queryParams, p: parseInt(page, 10) });
    window.location.hash = url;
  };

  const handleFileSelect = (e, fileId) => {
    e.preventDefault();
    return loadFile(fileId);
  };

  const handleLocaleChange = (userLocale) => {
    setLocale(userLocale);
    const expires = new Date();
    expires.setMonth(expires.getMonth() + 1);
    setCookies("USER_LOCALE", userLocale, {
      expires,
      secure: true,
    });
  };

  // listen for change in url/hash - handling client side. fires on pop/push url hash
  useEffect(() => {
    window.onpopstate = (e) => {
      const queryData = getHashParams();
      if (context !== queryData.t) {
        setContext(queryData.t);
      }
      if (queryData.fileItem) {
        // browser nav - back/forward nav. - load file
        if (!fileItem && loading) {
          return loadFile(queryData.fileItem);
        }
        return null;
      }
      if (!queryData.fileItem) {
        setFileItem(null);
      }
      setLoading(true);
      setMeta(null); // consider loading state - hidden vs no render!
      if (queryData.q !== q) {
        setQ(queryData.q);
      }
      if (queryData.t === contextType.EDUPIX) {
        eduSearch(
          queryData.q,
          queryData.f,
          pageSize,
          (queryData.p - 1) * pageSize,
          queryData.locale
        ).then((response) => {
          setLoading(false);
          setResults(response.hits);
          setMeta(response.meta);
        });
      }
      if (queryData.t === contextType.GDL) {
        gdlSearch(
          queryData.q,
          queryData.f,
          pageSize,
          (queryData.p - 1) * pageSize,
          queryData.locale
        ).then((response) => {
          setLoading(false);
          setResults(response.books);
          setMeta(response.meta);
        });
      }
    };
  }, [window.location.hash]);

  useEffect(() => {
    if (!firstUpdate.current) {
      const queryParams = getHashParams();
      const url = buildHashUrl({ ...queryParams, locale, p: 1, q: "" });
      setQ("");
      window.location.hash = url;
    }
  }, [locale]);

  useEffect(async () => {
    // initial search on load
    if (firstUpdate.current) {
      setLoading(true);
      firstUpdate.current = false;
      const queryData = getHashParams();
      const query = debouncedQ;
      const queryPage = queryData.p;
      const queryFilter = queryData.f;
      const type =  queryData.t;
      if (queryData.fileItem) {
        return loadFile(queryData.fileItem);
      }
      setQ(query);
      // TODO: initial GDL search?
      // if (parseInt(queryData.t) === 0) {
      if (queryData.t === contextType.EDUPIX) {
        return eduSearch(
          query,
          queryFilter,
          pageSize,
          (queryPage - 1) * pageSize,
          locale
        ).then((response) => {
          setLoading(false);
          setResults(response.hits);
          setMeta(response.meta);
        });
      }

      if (queryData.t === contextType.GDL) {
        gdlSearch(
          queryData.q,
          queryData.f,
          pageSize,
          (queryData.p - 1) * pageSize,
          queryData.locale
        ).then((response) => {
          setLoading(false);
          setResults(response.books);
          setMeta(response.meta);
        });
      }
    }
    const queryParams = getHashParams();
    const url = buildHashUrl({ ...queryParams, q, p: 1, f: "image" });
    window.location.hash = url;
  }, [debouncedQ]); // trigger search

  // console.log('meta.q', meta.q)
  return (
    <ThemeProvider theme={theme}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {fileItem && context === contextType.EDUPIX && (
          <>
            <FileItem item={fileItem} context={context} nonce={nonce} loadFile={loadFile} />
          </>
        )}
        {fileItem && context === contextType.GDL && (
          <>
            <GDLFileItem item={fileItem} context={context} nonce={nonce} loadFile={loadFile} />
          </>
        )}
        {!fileItem && (
          <>
            <SearchType handleTabChange={handleTabChange} tabValue={context} />
            <Container>
              <LocaleSelector
                onLocaleChange={handleLocaleChange}
                options={context === contextType.EDUPIX ? locales:gdlLocales}
                locale={storedLocale}
                color="rgba(0, 0, 0, 0.65)"
              />
              <InputSearch
                filter={"image"}
                // filterOptions={filterOptions}
                setFilter={handleFilterChange}
                query={q}
                isLoading={loading || q !== debouncedQ}
                onSearch={handleSearch}
              />
              {/* component, share ? */}
              <Typography color="textPrimary">
                <br />
                Enter a search word above to find relevant resourses
              </Typography>
              
              <p style={{ textAlign: "center" }}>
                {debouncedQ !== "" && meta && urlHashParams.f === "image" && (
                  <>
                    Searched for: {q}
                    <br />
                    <small>{meta.count} items found</small>
                  </>
                )}
              </p>
            </Container>
          </>
        )}
        {!fileItem && (
          <>
            <TabPanel value={contextType.EDUPIX} index={contextType.EDUPIX}>
              <ItemList>
                  {results && context === contextType.EDUPIX && results.map((item) => {
                    return (
                      <EdupixGalleryItem
                        key={item._id.toString()}
                        item={item}
                        itemLink={
                          <LinkItem
                            onClick={(e) =>
                              handleFileSelect(e, item._id.toString())
                            }
                            href={`/#file=${item._id.toString()}`}
                          />
                        }
                      />
                    );
                  })}
                <li></li>
              </ItemList>
            </TabPanel>
            <TabPanel value={contextType.GDL} index={contextType.GDL}>
              <ItemList>
                {results && context === contextType.GDL && results.map((item) => {
                    return (
                      <GDLGalleryItem 
                        key={item.postId}
                        item={item}
                        itemLink={
                          <LinkItem
                            onClick={(e) =>
                              handleFileSelect(e, item.postId.toString())
                            }
                            href={`/#file=${item.postId}`}
                          />
                        }
                      />
                    )
                })}
                <li></li>
              </ItemList> 
            </TabPanel>
            <PaginationWrapper>
              {meta && meta.count && meta.q && meta.q.contenttype ? (
                <Pagination
                  page={parseInt(urlHashParams.p, 10)}
                  count={Math.ceil(meta.count / pageSize)}
                  onChange={handlePageChange}
                  color="primary"
                  disabled={meta.count < pageSize}
                />
              ) : null}
            </PaginationWrapper>
          </>
        )}
      </MuiThemeProvider>
    </ThemeProvider>
  );
};
export default App;
